import { useEffect, useRef, useState, type JSX } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { decodeGithubState } from '@kirin/api-client/api/github';

/**
 * GitHub App Installation Callback Handler
 *
 * This page receives the redirect from GitHub after app installation.
 * It decodes the signed state token to determine which organization
 * to redirect to, then navigates to that org's settings page.
 *
 * Flow:
 * 1. GitHub redirects here with ?installation_id=...&state=<signed_token>
 * 2. We call POST /api/github/decode-state to get the organizationId
 * 3. Navigate to /:organizationId/settings with the callback params
 * 4. SettingsTab completes the installation by calling complete-installation
 *
 * Authentication requirement:
 * The decode-state endpoint requires session auth. If the user is logged out
 * when returning from GitHub (e.g., session expired, different browser), they
 * will see an error and be redirected home. This is acceptable because:
 * - The complete-installation endpoint also requires auth
 * - The user can retry by clicking "Connect GitHub" again
 * - OAuth flows inherently require the same authenticated session
 */
export default function GithubCallback(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const installationId = searchParams.get('installation_id');
    const setupAction = searchParams.get('setup_action');
    const state = searchParams.get('state'); // Signed state token

    async function handleCallback(): Promise<void> {
      if (!installationId || !state) {
        // Missing required parameters
        setError('Missing required parameters from GitHub callback');
        redirectTimeoutRef.current = setTimeout(
          () => navigate('/', { replace: true }),
          3000
        );
        return;
      }

      try {
        // Decode the signed state token to get the organization ID
        const { organizationId } = await decodeGithubState(apiClient, {
          state,
        });

        // Redirect to the correct organization's settings page
        // Pass along the callback parameters for complete-installation
        // Note: Don't set github=connected yet - SettingsTab will set it after
        // the backend call completes to avoid a race condition with refetch
        navigate(
          `/${organizationId}/settings?installation_id=${installationId}&setup_action=${setupAction || ''}&state=${encodeURIComponent(state)}`,
          { replace: true }
        );
      } catch (err) {
        console.error('[GitHub Callback] Failed to decode state token:', err);
        setError(
          'Failed to validate GitHub callback. The link may have expired.'
        );
        redirectTimeoutRef.current = setTimeout(
          () => navigate('/', { replace: true }),
          3000
        );
      }
    }

    void handleCallback();

    return (): void => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-xl font-semibold mb-2 text-destructive">
            GitHub Setup Error
          </h1>
          <p className="text-muted-foreground">{error}</p>
          <p className="text-sm text-muted-foreground mt-2">
            Redirecting to home...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <h1 className="text-xl font-semibold mb-2">
          Completing GitHub Setup...
        </h1>
        <p className="text-muted-foreground">
          Please wait while we redirect you.
        </p>
      </div>
    </div>
  );
}
